// apps/web/src/pages/ImportPage.tsx
// AI 导题首页：参考小程序风格，直接上传文件，自动生成题库

import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { aiImportStartApi, aiImportJobStatusApi, aiImportListJobsApi } from "@/api/aiImport";
import { listBanks } from "@/api/banks";
import { batchImportQuestionsApi } from "@/api/questions";
import type { BankDTO, CreateQuestionRequest } from "@examfast/shared-types";
import type { AIImportJob, AIJobStatus } from "@/api/aiImport";
import ImportProgress, { formatTime } from "@/components/aiImport/ImportProgress";

type Step = "upload" | "processing" | "completed" | "failed";

const TYPE_LABELS: Record<string, string> = {
  docx: "Word",
  xlsx: "Excel",
  xls: "Excel",
  pdf: "PDF",
  txt: "TXT",
  md: "TXT",
};

export default function ImportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<AIImportJob | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const pollingRef = useRef(true);
  const abortRef = useRef(false);
  const [recentJobs, setRecentJobs] = useState<AIImportJob[]>([]);
  const [banks, setBanks] = useState<BankDTO[]>([]);
  const [selectedBankId, setSelectedBankId] = useState(searchParams.get("bankId") ?? "");
  const [jsonImporting, setJsonImporting] = useState(false);

  // 加载最近导入记录
  useEffect(() => {
    aiImportListJobsApi(1, 5)
      .then((res) => setRecentJobs(res.items))
      .catch(() => setRecentJobs([]));
  }, []);

  useEffect(() => {
    listBanks({ page_size: 100 })
      .then((res) => {
        setBanks(res.items);
        setSelectedBankId((current) => current || res.items[0]?.id || "");
      })
      .catch(() => setBanks([]));
  }, []);

  // 组件卸载时停止轮询
  useEffect(() => {
    return () => {
      pollingRef.current = false;
      abortRef.current = true;
    };
  }, []);

  const detectType = (name: string): string => {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    return TYPE_LABELS[ext] || "文档";
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await startImport(f);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    await startImport(f);
  };

  const startImport = async (f: File) => {
    abortRef.current = false;
    pollingRef.current = true;
    setFile(f);
    setStep("processing");
    setElapsed(0);

    try {
      // 浏览器端解析文件
      const { parseFile } = await import("@/utils/fileParsers");
      const parsed = await parseFile(f);

      if (abortRef.current) return;

      // 创建导入任务
      const { jobId, bankName } = await aiImportStartApi({
        text: parsed.text,
        fileName: f.name,
        fileSize: f.size,
        fileType: detectType(f.name),
      });

      toast.success(`开始导入「${bankName}」`);

      // 轮询任务状态
      const maxWaitMs = 300_000; // 5 分钟超时
      const pollInterval = 2000;
      const startTime = Date.now();

      while (pollingRef.current && !abortRef.current) {
        const elapsedMs = Date.now() - startTime;
        setElapsed(elapsedMs);

        if (elapsedMs > maxWaitMs) {
          throw new Error("AI 导入超时，请前往生成记录查看进度");
        }

        const status = await aiImportJobStatusApi(jobId);
        setJob(status);

        if (status.status === "completed") {
          setStep("completed");
          toast.success(`导入完成：${status.successCount} 道题`);
          // 刷新最近记录
          aiImportListJobsApi(1, 5).then((res) => setRecentJobs(res.items));
          return;
        }

        if (status.status === "failed") {
          setStep("failed");
          throw new Error(status.error ?? "AI 导入失败");
        }

        await new Promise((r) => setTimeout(r, pollInterval));
      }

      if (!abortRef.current) {
        toast("已取消导入");
        setStep("upload");
      }
    } catch (err) {
      if (!abortRef.current) {
        toast.error(err instanceof Error ? err.message : "导入失败");
        if (step !== "completed") setStep("failed");
      }
    }
  };

  const handleCancel = () => {
    pollingRef.current = false;
    abortRef.current = true;
    setStep("upload");
    setFile(null);
    setJob(null);
    setElapsed(0);
  };

  const handleJsonFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!selectedBankId) {
      toast.error("请先选择目标题库");
      return;
    }

    setJsonImporting(true);
    try {
      const raw = JSON.parse(await file.text()) as unknown;
      const questions = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object" && Array.isArray((raw as { questions?: unknown }).questions)
          ? (raw as { questions: unknown[] }).questions
          : null;
      if (!questions || questions.length === 0) throw new Error("JSON 中没有找到 questions 题目数组");
      if (questions.length > 1000) throw new Error("单次最多导入 1000 道题");

      const result = await batchImportQuestionsApi(selectedBankId, questions as CreateQuestionRequest[]);
      toast.success(`导入完成：成功 ${result.success_count} 道，失败 ${result.fail_count} 道`);
      if (result.failures.length > 0) {
        console.warn("JSON 导入失败题目", result.failures);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "JSON 导入失败");
    } finally {
      setJsonImporting(false);
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes}B`;
    return `${(bytes / 1024).toFixed(2)}KB`;
  };

  const formatTime = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}秒`;
    const min = Math.floor(sec / 60);
    const rem = sec % 60;
    return `${min}分${rem}秒`;
  };

  const formatDate = (ts?: number) => {
    if (!ts) return "";
    const d = new Date(ts);
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const formatImportError = (error?: string | null) => {
    if (!error) return "请检查文件后重试";
    if (error.includes("DEEPSEEK_INVALID_JSON") || error.includes("DEEPSEEK_SCHEMA_VALIDATION")) {
      return "AI 返回内容不完整，已尝试分段修复；请重新上传或减少单次题量";
    }
    if (error.includes("DEEPSEEK_EMPTY_RESPONSE")) return "AI 暂时没有返回结果，请稍后重试";
    if (error.includes("SOURCE_TEXT_EMPTY")) return "文件内容为空，请检查文件后重试";
    if (error.includes("DEEPSEEK_API_ERROR")) return "AI 服务暂时不可用，请稍后重试";
    if (error.includes("DEEPSEEK_IMPORT_FAILED")) return "部分文档片段无法识别，请拆分文件后重试";
    return error.length > 120 ? "导入失败，请检查文件内容后重试" : error;
  };

  const formatFailureLog = (logs?: string | null) => {
    if (!logs) return "";
    const diagnostics = logs
      .split("\n")
      .filter((line) => line.includes("[diagnostic]"))
      .join("\n")
      .trim();
    return (diagnostics || logs.trim()).slice(-4000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-gray-700 text-sm flex items-center gap-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <Link
            to="/import/history"
            className="text-xs px-3 py-1.5 bg-blue-100 text-blue-600 rounded-full font-medium"
          >
            生成记录
          </Link>
          <button className="text-xs px-3 py-1.5 bg-blue-100 text-blue-600 rounded-full font-medium">
            导题说明
          </button>
        </div>
      </div>

      <div className="px-4 pt-6 pb-4 space-y-5">
        {/* 标题区 */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-3xl font-black italic bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              AI导题
            </span>
            <span className="px-2 py-0.5 bg-pink-500 text-white text-xs font-bold rounded-lg transform -skew-x-12">
              免费
            </span>
          </div>
          <p className="text-sm text-gray-500">复杂格式1分钟完成</p>
        </div>

        {/* 功能标签 */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {["AI解析", "生成精简题", "错题巩固", "模拟考试"].map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-white border border-blue-100 text-blue-600 text-xs rounded-full shadow-sm"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* 上传卡片 */}
        <section className="bg-white rounded-2xl shadow-lg shadow-blue-100/50 p-5 border border-blue-50">
          {step === "upload" && (
            <>
              {/* 文件格式图标 */}
              <div className="flex items-center justify-center gap-5 mb-5">
                {[
                  { icon: WordIcon, label: "Word", color: "#3B82F6" },
                  { icon: ExcelIcon, label: "Excel", color: "#10B981" },
                  { icon: PdfIcon, label: "PDF", color: "#EF4444" },
                  { icon: TxtIcon, label: "TXT", color: "#6B7280" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-50 shadow-sm">
                      <Icon color={color} />
                    </div>
                    <span className="text-[10px] text-gray-500">{label}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl text-base font-medium shadow-blue-200 shadow-lg btn-tap flex items-center justify-center gap-2"
              >
                <UploadIcon />
                上传文档
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.xlsx,.xls,.pdf,.txt,.md"
                className="hidden"
                onChange={handleFileSelect}
              />

              <div className="mt-4 text-center space-y-1">
                <p className="text-xs text-blue-600 font-medium">剩余9次</p>
                <p className="text-[10px] text-gray-400">20+文档格式随心传，分享好友一起学</p>
              </div>
            </>
          )}

          {step === "processing" && file && (
            <div className="py-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                <SpinnerLarge />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">AI 正在导入中...</h3>
              <p className="text-sm text-gray-500 mb-5">
                {file.name} · {formatSize(file.size)} · 已用 {formatTime(elapsed)}
              </p>
              <div className="px-1">
                <ImportProgress progress={job?.progress} logs={job?.logs} />
              </div>
              <button onClick={handleCancel} className="text-sm text-danger mt-5">
                取消导入
              </button>
            </div>
          )}

          {step === "completed" && job && (
            <div className="py-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                <CheckIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">导入完成</h3>
              <p className="text-sm text-gray-500 mb-4">
                {job.bankName} · 识别 {job.questionCount} 题 · 成功 {job.successCount} 题
              </p>
              <div className="flex gap-3">
                {job.bankId && (
                  <button
                    onClick={() => navigate(`/banks/${job.bankId}`)}
                    className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-medium btn-tap"
                  >
                    查看题库
                  </button>
                )}
                <button
                  onClick={handleCancel}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium btn-tap"
                >
                  继续导入
                </button>
              </div>
            </div>
          )}

          {step === "failed" && (
            <div className="py-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <CrossIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">导入失败</h3>
              <p className="text-sm text-gray-500 mb-4">{formatImportError(job?.error)}</p>
              {formatFailureLog(job?.logs) ? (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-100 p-3 text-left">
                  <p className="text-xs font-medium text-red-700 mb-2">失败诊断日志</p>
                  <pre className="text-[11px] font-mono leading-relaxed text-red-700 whitespace-pre-wrap break-words max-h-72 overflow-y-auto">
                    {formatFailureLog(job?.logs)}
                  </pre>
                </div>
              ) : null}
              <button
                onClick={handleCancel}
                className="w-full py-2.5 bg-primary text-white rounded-xl text-sm font-medium btn-tap"
              >
                重新上传
              </button>
            </div>
          )}
        </section>

        {/* JSON 批量导入 */}
        <section className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">JSON 批量导入</h2>
              <p className="mt-1 text-xs leading-5 text-gray-500">将符合格式的 JSON 题目导入已有题库，不经过 AI 解析。</p>
            </div>
            <span className="rounded-lg bg-emerald-50 px-2 py-1 text-[11px] text-emerald-600">精准导入</span>
          </div>
          <select
            value={selectedBankId}
            onChange={(event) => setSelectedBankId(event.target.value)}
            disabled={jsonImporting || banks.length === 0}
            className="mt-4 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-emerald-500"
          >
            {banks.length === 0 ? <option value="">暂无题库，请先创建</option> : banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
          </select>
          <button
            type="button"
            onClick={() => jsonInputRef.current?.click()}
            disabled={jsonImporting || !selectedBankId}
            className="mt-3 w-full rounded-xl bg-emerald-500 py-3 text-sm font-medium text-white shadow-sm disabled:opacity-50"
          >
            {jsonImporting ? "导入中..." : "选择 JSON 文件并导入"}
          </button>
          <input ref={jsonInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleJsonFile} />
          <p className="mt-2 text-center text-[11px] text-gray-400">支持 {`{"questions": [...]}`} 或直接上传题目数组</p>
        </section>

        {/* 最近上传 */}
        {recentJobs.length > 0 && step === "upload" && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">最近上传</h2>
              <Link to="/import/history" className="text-xs text-blue-600">
                全部
              </Link>
            </div>
            <div className="space-y-3">
              {recentJobs.slice(0, 3).map((job) => (
                <div
                  key={job.jobId}
                  onClick={() => job.bankId && navigate(`/banks/${job.bankId}`)}
                  className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-[0.99] transition-transform"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <FileTypeIcon type={job.fileType} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{job.fileName}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          job.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : job.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {job.status === "completed" ? "已完成" : job.status === "failed" ? "失败" : "处理中"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(job.createdAt)} · 识别 {job.questionCount} 题 · 成功 {job.successCount} 题
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 底部功能区 */}
        <section>
          <div className="grid grid-cols-3 gap-3">
            <Link
              to="/banks/new"
              className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-green-50 flex items-center justify-center">
                <ManualIcon />
              </div>
              <h3 className="text-sm font-medium text-gray-900">手动导题</h3>
              <p className="text-[10px] text-gray-400 mt-0.5">单题/文档录入</p>
            </Link>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center opacity-70">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-orange-50 flex items-center justify-center">
                <HumanIcon />
              </div>
              <h3 className="text-sm font-medium text-gray-900">人工导题</h3>
              <p className="text-[10px] text-gray-400 mt-0.5">专业内容团队录入</p>
            </div>
            <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 text-center opacity-70">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-50 flex items-center justify-center">
                <CameraIcon />
              </div>
              <h3 className="text-sm font-medium text-gray-900">拍照录题</h3>
              <p className="text-[10px] text-gray-400 mt-0.5">自动提取图片试题</p>
            </div>
          </div>
        </section>

        {/* 底部链接 */}
        <div className="flex items-center justify-between text-xs text-blue-600 px-1">
          <button className="flex items-center gap-1">
            下载导入模版
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button className="flex items-center gap-1">
            试题与答案分开上传
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function SpinnerLarge() {
  return <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />;
}

function CheckIcon() {
  return (
    <svg className="w-8 h-8 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function WordIcon({ color }: { color: string }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 7h3l2 5 2-5h3v10h-2V10l-2 4-2-4v7H7V7z" fill={color} />
    </svg>
  );
}

function ExcelIcon({ color }: { color: string }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z" fill={color} />
    </svg>
  );
}

function PdfIcon({ color }: { color: string }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 7h6v2H7V7zm0 4h10v2H7v-2zm0 4h8v2H7v-2z" fill={color} />
    </svg>
  );
}

function TxtIcon({ color }: { color: string }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 8h10v1.5H7V8zm0 3h10v1.5H7V11zm0 3h7v1.5H7v-1.5z" fill={color} />
    </svg>
  );
}

function FileTypeIcon({ type }: { type: string | null }) {
  const color = type === "Word" ? "#3B82F6" : type === "Excel" ? "#10B981" : type === "PDF" ? "#EF4444" : "#6B7280";
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={color} opacity="0.15" />
      <path d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z" fill={color} />
    </svg>
  );
}

function ManualIcon() {
  return (
    <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function HumanIcon() {
  return (
    <svg className="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M16 11l2 2 4-4" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
